import React, { useMemo, useState } from "react";
import { resolveQuestContent } from "../lib/resolveQuestContent";

type Props = {
  quest: any;
  language?: string;
};

type Mood = "DEFAULT" | "ROMANTIC" | "PLAYFUL" | "BOLD";

function chipClass(kind: string) {
  const k = String(kind || "").toLowerCase().trim();
  if (k === "adult") return "bg-rose-500/15 text-rose-300 border border-rose-400/30";
  if (k === "spicy") return "bg-orange-500/15 text-orange-300 border border-orange-400/30";
  if (k === "basis" || k === "base") return "bg-slate-500/15 text-slate-200 border border-slate-400/20";
  return "bg-violet-500/15 text-violet-200 border border-violet-400/30";
}

function pill(active: boolean) {
  return active
    ? "rounded-full px-3 py-1.5 text-xs font-semibold border border-pink-400/40 bg-pink-500/20 text-pink-100"
    : "rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-700 bg-slate-900/80 text-slate-300 hover:border-slate-500";
}

function sectionLabel(label: string) {
  return (
    <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </div>
  );
}

export default function QuestContentCard({ quest, language = "en" }: Props) {
  const meta = quest?.meta && typeof quest.meta === "object" ? quest.meta : {};
  const styleKeys = Object.keys(meta?.styles || {}).map((x) => String(x).toUpperCase());

  const allowedMoods = useMemo<Mood[]>(() => {
    const out: Mood[] = ["DEFAULT"];
    if (styleKeys.includes("ROMANTIC")) out.push("ROMANTIC");
    if (styleKeys.includes("PLAYFUL")) out.push("PLAYFUL");
    if (styleKeys.includes("BOLD")) out.push("BOLD");
    return out;
  }, [quest]);

  const initialMood = useMemo<Mood>(() => {
    const raw = String(meta?.styleDefault || "").toUpperCase().trim();
    if (raw === "ROMANTIC" || raw === "PLAYFUL" || raw === "BOLD") return raw as Mood;
    return "DEFAULT";
  }, [quest]);

  const [mood, setMood] = useState<Mood>(initialMood);
  const [variation, setVariation] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<"role1" | "role2" | "">("");

  const q = resolveQuestContent(quest, language, {
    style: mood === "DEFAULT" ? undefined : mood,
    variation: variation || undefined,
  });

  const questModes = Array.isArray(q.modes)
    ? q.modes.map((m) => String(m || "").toLowerCase().trim()).filter(Boolean)
    : [];
  const isSoloOnlyQuest =
    questModes.includes("solo") && questModes.every((m) => m === "solo");
  const hasPartnerRoles = !isSoloOnlyQuest && !!q.hasRole2;
  const role1Label = q.role1Name || "Role 1";
  const role2Label = q.role2Name || "Role 2";
  const selectedRoleLabel =
    selectedRole === "role1" ? role1Label : selectedRole === "role2" ? role2Label : "";

  return (
    <div className="overflow-hidden rounded-[22px] border border-slate-800 bg-slate-950/70 shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
      <div className="border-b border-slate-800 bg-slate-900/80 p-5">
        <div className="mb-3 flex flex-wrap gap-2">
          {!!q.packCode && (
            <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${chipClass(q.packCode)}`}>
              {q.packCode}
            </span>
          )}

          {q.isAdult && (
            <span className="rounded-full px-3 py-1 text-[11px] font-semibold bg-rose-500/15 text-rose-300 border border-rose-400/30">
              adult
            </span>
          )}

          {q.modes.map((m) => (
            <span
              key={m}
              className="rounded-full px-3 py-1 text-[11px] font-semibold bg-fuchsia-500/10 text-fuchsia-200 border border-fuchsia-400/20"
            >
              {m}
            </span>
          ))}
        </div>

        <h2 className="text-2xl font-extrabold text-white">
          {q.title || "Untitled quest"}
        </h2>

        {!!q.summary && (
          <p className="mt-2 text-sm leading-6 text-slate-300">
            {q.summary}
          </p>
        )}

        {allowedMoods.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {allowedMoods.map((m) => (
              <button
                key={m}
                type="button"
                className={pill(mood === m)}
                onClick={() => setMood(m)}
              >
                {m === "DEFAULT" ? "Default" : m.charAt(0) + m.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        )}

        {q.availableVariations.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={pill(!variation)}
              onClick={() => setVariation("")}
            >
              Default
            </button>

            {q.availableVariations.map((v) => (
              <button
                key={v.key}
                type="button"
                className={pill(variation === v.key)}
                onClick={() => setVariation(v.key)}
              >
                {v.label || v.key}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-5 p-5">
        {q.warnings.length > 0 && (
          <section className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-300">
              Warning
            </div>
            <div className="space-y-2">
              {q.warnings.map((w, idx) => (
                <div key={`${w}-${idx}`} className="whitespace-pre-wrap text-sm leading-6 text-amber-100">
                  {w}
                </div>
              ))}
            </div>
          </section>
        )}

        {!!q.body && (
          <section>
            {sectionLabel("Body")}
            <div className="whitespace-pre-wrap text-sm leading-7 text-slate-200">
              {q.body}
            </div>
          </section>
        )}

        {!!q.variationNote && (
          <section className="rounded-2xl border border-sky-500/25 bg-sky-500/10 p-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-sky-300">
              Variation note
            </div>
            <div className="whitespace-pre-wrap text-sm leading-6 text-sky-100">
              {q.variationNote}
            </div>
          </section>
        )}

        {hasPartnerRoles && (
          <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Choose your role
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Pick the side that fits you. The detailed prompt stays focused on the quest instead of showing both scripts at once.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setSelectedRole("role1")}
                className={
                  "rounded-2xl border p-4 text-left transition " +
                  (selectedRole === "role1"
                    ? "border-pink-400/60 bg-pink-500/10"
                    : "border-slate-800 bg-slate-950/50 hover:border-pink-400/30")
                }
              >
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  Role 1
                </div>
                <div className="mt-1 text-base font-bold text-slate-100">
                  {role1Label}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-400">
                  Choose this if this side feels closest to you.
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedRole("role2")}
                className={
                  "rounded-2xl border p-4 text-left transition " +
                  (selectedRole === "role2"
                    ? "border-pink-400/60 bg-pink-500/10"
                    : "border-slate-800 bg-slate-950/50 hover:border-pink-400/30")
                }
              >
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  Role 2
                </div>
                <div className="mt-1 text-base font-bold text-slate-100">
                  {role2Label}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-400">
                  Choose this if this side fits your partner better.
                </div>
              </button>
            </div>

            {!!selectedRoleLabel && (
              <div className="mt-3 rounded-2xl border border-pink-400/25 bg-pink-500/10 px-4 py-3 text-sm text-pink-100">
                Selected: <span className="font-semibold">{selectedRoleLabel}</span>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
